import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Mail, Phone, CheckCircle, XCircle } from 'lucide-react';
import { judgeService } from '../services/supabaseService';
import { useNotifications } from '../components/shared/useNotifications';
import { useConfirm } from '../components/shared/ConfirmContext';
import '../components/admin/AdminView.css';
import logger from '../utils/logger';

const JudgesView = ({
  judges = [],
}) => {
  const notify = useNotifications();
  const confirm = useConfirm();
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingJudgeId, setEditingJudgeId] = useState(null);
  const [localJudges, setLocalJudges] = useState(judges);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'pending'
  const [loadingPending, setLoadingPending] = useState(false);
  const [judgeForm, setJudgeForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    password: ''
  });

  // Hash password function
  const hashPassword = async (pwd) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(pwd);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  };

  // Actualizar jueces locales cuando cambien los props
  useEffect(() => {
    setLocalJudges(judges);
  }, [judges]);

  // Cargar y actualizar solicitudes pendientes cuando cambien los jueces locales
  useEffect(() => {
    // Incluir tanto los jueces inactivos de localJudges como los de getPendingAccessRequests
    if (localJudges) {
      const inactiveJudges = localJudges.filter(judge => 
        judge.is_active === false && judge.is_requested === true
      );
      setPendingRequests(inactiveJudges);
    }
    setLoadingPending(false);
  }, [localJudges]);

  // Obtener jueces inactivos (sin solicitud de acceso)
  const inactiveJudges = useMemo(() => {
    return (localJudges || []).filter(judge => 
      judge.is_active === false && (judge.is_requested === false || !judge.is_requested)
    );
  }, [localJudges]);

  // Filtrar jueces por búsqueda
  const filteredJudges = useMemo(() => {
    let judges = localJudges || [];
    
    // Filtrar por pestaña activa (solo jueces activos en la pestaña 'active')
    if (activeTab === 'active') {
      judges = judges.filter(judge => judge.is_active === true);
    }
    
    // Filtrar por búsqueda
    if (searchTerm.trim()) {
      judges = judges.filter(judge =>
        judge.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        judge.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return judges;
  }, [localJudges, searchTerm, activeTab]);

  const handleEdit = (judge) => {
    setEditingJudgeId(judge.id);
    setJudgeForm({
      name: judge.name,
      email: judge.email,
      phone: judge.phone || '',
      address: judge.address || '',
      password: ''
    });
    setShowForm(true);
  };

  const handleDelete = async (judge) => {
    const ok = await confirm({
      title: 'Eliminar Juez',
      message: `¿Estás seguro de que deseas eliminar al juez ${judge.name}?`,
      type: 'danger',
      confirmText: 'Eliminar'
    });
    if (!ok) return;
    
    const result = await judgeService.delete(judge.id);
    if (result.error) {
      notify.error('Error al eliminar el juez: ' + result.error);
    } else {
      setLocalJudges(localJudges.filter(j => j.id !== judge.id));
      notify.success('Juez eliminado correctamente');
    }
  };

  const handleSave = async () => {
    if (!judgeForm.name.trim() || !judgeForm.email.trim()) {
      notify.warning('Por favor completa los campos requeridos (Nombre y Email)');
      return;
    }

    try {
      let result;
      if (editingJudgeId) {
        // Actualizar juez existente
        const updateData = {
          name: judgeForm.name,
          email: judgeForm.email,
          phone: judgeForm.phone,
          address: judgeForm.address
        };
        
        // Si se proporciona contraseña, hashearla
        if (judgeForm.password.trim()) {
          updateData.password_hash = await hashPassword(judgeForm.password);
        }
        
        result = await judgeService.update(editingJudgeId, updateData);
      } else {
        // Crear nuevo juez
        if (!judgeForm.password.trim()) {
          notify.warning('Por favor ingresa una contraseña para el nuevo juez');
          return;
        }
        
        const hashedPassword = await hashPassword(judgeForm.password);
        result = await judgeService.create({
          name: judgeForm.name,
          email: judgeForm.email,
          phone: judgeForm.phone,
          address: judgeForm.address,
          password_hash: hashedPassword
        });
      }

      if (result.error) {
        notify.error('Error al guardar el juez: ' + result.error);
      } else {
        // Actualizar lista local sin recargar
        if (editingJudgeId) {
          // Actualizar juez existente
          setLocalJudges(localJudges.map(j => 
            j.id === editingJudgeId 
              ? { ...j, name: judgeForm.name, email: judgeForm.email, phone: judgeForm.phone, address: judgeForm.address }
              : j
          ));
        } else {
          // Crear nuevo juez - el resultado debería tener el nuevo juez con ID
          if (result.data) {
            setLocalJudges([...localJudges, result.data]);
          }
        }
        notify.success(editingJudgeId ? 'Juez actualizado correctamente' : 'Juez creado correctamente');
        resetForm();
      }
    } catch (error) {
      logger.error('Error guardando juez:', error?.message);
      notify.error('Error al guardar el juez: ' + error.message);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingJudgeId(null);
    setJudgeForm({
      name: '',
      email: '',
      phone: '',
      address: '',
      password: ''
    });
  };

  // Funciones para gestionar solicitudes de acceso
  const handleApproveRequest = async (judgeId) => {
    const ok = await confirm({
      title: 'Aprobar Solicitud',
      message: '¿Deseas aprobar esta solicitud de acceso?',
      type: 'info',
      confirmText: 'Aprobar'
    });
    if (!ok) return;
    
    const result = await judgeService.activateJudge(judgeId);
    if (result.error) {
      notify.error('Error al aprobar la solicitud: ' + result.error);
    } else {
      setLocalJudges(localJudges.map(j =>
        j.id === judgeId 
          ? { ...j, is_active: true }
          : j
      ));
      notify.success('Solicitud aprobada correctamente');
    }
  };

  const handleRejectRequest = async (judgeId) => {
    const ok = await confirm({
      title: 'Rechazar Solicitud',
      message: '¿Deseas rechazar esta solicitud de acceso? El juez será eliminado.',
      type: 'danger',
      confirmText: 'Rechazar'
    });
    if (!ok) return;
    
    const result = await judgeService.delete(judgeId);
    if (result.error) {
      notify.error('Error al rechazar la solicitud: ' + result.error);
    } else {
      setLocalJudges(localJudges.filter(j => j.id !== judgeId));
      notify.success('Solicitud rechazada correctamente');
    }
  };

  const handleToggleJudgeStatus = async (judgeId, currentStatus) => {
    const action = currentStatus ? 'desactivar' : 'activar';
    const ok = await confirm({
      title: `${action === 'activar' ? 'Activar' : 'Desactivar'} Juez`,
      message: `¿Deseas ${action} a este juez?`,
      type: 'warning',
      confirmText: action === 'activar' ? 'Activar' : 'Desactivar'
    });
    if (!ok) return;
    
    const result = currentStatus 
      ? await judgeService.deactivateJudge(judgeId)
      : await judgeService.activateJudge(judgeId);
    
    if (result.error) {
      notify.error(`Error al ${action} el juez: ` + result.error);
    } else {
      setLocalJudges(localJudges.map(j =>
        j.id === judgeId 
          ? { ...j, is_active: !currentStatus }
          : j
      ));
      notify.success(`Juez ${action}ado correctamente`);
    }
  };


  return (
    <div className="view-container">
      <div className="view-header">
        <h1>👨‍⚖️ Gestión de Jueces</h1>
        <button
          onClick={() => {
            setEditingJudgeId(null);
            setShowForm(true);
          }}
          className="btn btn-primary"
        >
          <Plus size={18} />
          Nuevo Juez
        </button>
      </div>

      {/* Tabs — modern underlined */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        {[
          { id: 'active', label: `Jueces Activos (${filteredJudges?.length || 0})` },
          { id: 'pending', label: `Solicitudes de Acceso (${pendingRequests?.length || 0})`, badge: pendingRequests?.length },
          { id: 'inactive', label: `Jueces Inactivos (${inactiveJudges?.length || 0})` },
        ].map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: 'transparent',
                color: isActive ? '#8b5cf6' : '#64748b',
                border: 'none',
                padding: '10px 16px',
                marginBottom: -1,
                borderBottom: isActive ? '2px solid #8b5cf6' : '2px solid transparent',
                fontWeight: isActive ? 700 : 500,
                fontSize: '0.88rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                transition: 'color 0.15s, border-color 0.15s',
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#0f172a'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = '#64748b'; }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'active' && (
        <>
          {/* Búsqueda */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: '12px', top: '10px', color: '#9ca3af' }} />
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field"
                style={{ paddingLeft: '40px' }}
              />
            </div>
          </div>

          {/* Tabla de Jueces Activos */}
          <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                      Nombre
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                      Email
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                      Teléfono
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                      Estado
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJudges && filteredJudges.length > 0 ? (
                    filteredJudges.map((judge, idx) => (
                      <tr
                        key={judge.id}
                        style={{
                          borderBottom: '1px solid #e5e7eb',
                          background: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
                        onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? '#ffffff' : '#f9fafb'}
                      >
                        <td style={{ padding: '12px 16px', fontSize: '0.9rem', fontWeight: '500', color: '#111827' }}>
                          {judge.name}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#6b7280' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Mail size={14} />
                            {judge.email}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#6b7280' }}>
                          {judge.phone ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Phone size={14} />
                              {judge.phone}
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.9rem' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            background: judge.is_active ? '#dcfce7' : '#fee2e2',
                            color: judge.is_active ? '#166534' : '#991b1b'
                          }}>
                            {judge.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                          <button
                            onClick={() => handleEdit(judge)}
                            className="btn-icon"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleToggleJudgeStatus(judge.id, judge.is_active)}
                            className={`btn-icon ${judge.is_active ? 'danger' : 'success'}`}
                            title={judge.is_active ? 'Desactivar' : 'Activar'}
                          >
                            {judge.is_active ? <XCircle size={16} /> : <CheckCircle size={16} />}
                          </button>
                          <button
                            onClick={() => handleDelete(judge)}
                            className="btn-icon danger"
                            title="Eliminar"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                        No hay jueces registrados
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Resumen */}
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            background: '#f0f9ff',
            borderRadius: '8px',
            borderLeft: '4px solid #0284c7',
            fontSize: '0.875rem',
            color: '#0c4a6e'
          }}>
            <strong>Total de jueces activos:</strong> {filteredJudges?.length || 0}
          </div>
        </>
      )}

      {activeTab === 'pending' && (
        <>
          {/* Solicitudes de Acceso */}
          <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            {loadingPending ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                Cargando solicitudes...
              </div>
            ) : pendingRequests && pendingRequests.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                        Nombre
                      </th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                        Email
                      </th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                        Teléfono
                      </th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                        Fecha de Solicitud
                      </th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRequests.map((request, idx) => (
                      <tr
                        key={request.id}
                        style={{
                          borderBottom: '1px solid #e5e7eb',
                          background: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#fffbeb'}
                        onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? '#ffffff' : '#f9fafb'}
                      >
                        <td style={{ padding: '12px 16px', fontSize: '0.9rem', fontWeight: '500', color: '#111827' }}>
                          {request.name}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#6b7280' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Mail size={14} />
                            {request.email}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#6b7280' }}>
                          {request.phone ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Phone size={14} />
                              {request.phone}
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#6b7280' }}>
                          {request.created_at ? (
                            new Date(request.created_at).toLocaleDateString('es-CL', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })
                          ) : (
                            'Sin fecha'
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                          <button
                            onClick={() => handleApproveRequest(request.id)}
                            className="btn-icon success"
                            title="Aprobar"
                          >
                            <CheckCircle size={16} />
                          </button>
                          <button
                            onClick={() => handleRejectRequest(request.id)}
                            className="btn-icon danger"
                            title="Rechazar"
                          >
                            <XCircle size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                No hay solicitudes de acceso pendientes
              </div>
            )}
          </div>

          {/* Resumen */}
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            background: '#fef3c7',
            borderRadius: '8px',
            borderLeft: '4px solid #f59e0b',
            fontSize: '0.875rem',
            color: '#92400e'
          }}>
            <strong>Solicitudes de acceso pendientes:</strong> {pendingRequests?.length || 0}
          </div>
        </>
      )}

      {activeTab === 'inactive' && (
        <>
          {/* Jueces Inactivos */}
          <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            {inactiveJudges && inactiveJudges.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                        Nombre
                      </th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                        Email
                      </th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                        Teléfono
                      </th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {inactiveJudges.map((judge, idx) => (
                      <tr
                        key={judge.id}
                        style={{
                          borderBottom: '1px solid #e5e7eb',
                          background: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#fef3c7'}
                        onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? '#ffffff' : '#f9fafb'}
                      >
                        <td style={{ padding: '12px 16px', fontSize: '0.9rem', fontWeight: '500', color: '#111827' }}>
                          {judge.name}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#6b7280' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Mail size={14} />
                            {judge.email}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#6b7280' }}>
                          {judge.phone ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Phone size={14} />
                              {judge.phone}
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                          <button
                            onClick={() => handleToggleJudgeStatus(judge.id, judge.is_active)}
                            className="btn-icon success"
                            title="Activar"
                          >
                            <CheckCircle size={16} />
                          </button>
                          <button
                            onClick={() => handleEdit(judge)}
                            className="btn-icon"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(judge)}
                            className="btn-icon danger"
                            title="Eliminar"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                No hay jueces inactivos
              </div>
            )}
          </div>

          {/* Resumen */}
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            background: '#fee2e2',
            borderRadius: '8px',
            borderLeft: '4px solid #dc2626',
            fontSize: '0.875rem',
            color: '#7f1d1d'
          }}>
            <strong>Jueces inactivos:</strong> {inactiveJudges?.length || 0}
          </div>
        </>
      )}
    </div>
  );
};

export default JudgesView;
